/* custom-mamacool-registry.js
   Behaviour for the "Not due yet? Save it for later" chip and its sheet
   (snippets/custom-mamacool-registry.liquid, README-registry.md).
   - Shows only when the country flag is on (data-enabled, from the Airtable
     countries table) OR the URL has ?feature=registry (preview; kept for the
     session in the mc_feature cookie; ?feature=registry,off clears it).
   - Hides itself when a discount code is in play this session (Shopify's
     discount_code cookie) or the shopper already saved a reminder (mc_registry).
   - The sheet is the hq.mamacool.com reminder card (README-links.md):
     month chips → day grid + readout → email, posting an exact due_date to
     api/reminder. The picker itself is the shared custom-mamacool-picker.js
     (also used by the due-date section); this file is the sheet around it.
   - Phones (max-width 600px): the <dialog> is opened non-modally, moved to
     the body and positioned absolutely at the scroll offset, because iOS
     Chrome clips the fixed/top layer a toolbar's height short of the screen
     while the toolbar is hidden and page content is the only thing that
     paints there. Everything a modal gives for free is done by hand: the
     theme's scroll lock, a focus trap, inert page, Escape, back button.
   - Desktop: a plain modal dialog.
   - Fires window event "mc:registry" {action} so pixels can listen; counts
     open and reminder in the links click log via sendBeacon. */
(function () {
  var root = document.querySelector('[data-mc-registry]:not([data-mc-init])');
  if (!root) return;
  root.setAttribute('data-mc-init', '');

  var LOG_URL = 'https://api.mamacool.com/api/links';
  var PHONE_QUERY = '(max-width: 600px)';

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function setCookie(name, value, days) {
    document.cookie = name + '=' + encodeURIComponent(value) + (days ? '; max-age=' + (days * 86400) : '') + '; path=/; SameSite=Lax; Secure';
  }
  function clearCookie(name) { document.cookie = name + '=; max-age=0; path=/; SameSite=Lax; Secure'; }

  // Every step is counted in the links click log (api/links, Supabase
  // link_events) as registry-<step> with the product handle as source.
  // sendBeacon with a text/plain body: no CORS preflight, queued by the
  // browser, survives the in-app browser being dismissed. fetch keepalive
  // is the fallback for browsers without it.
  var COUNTED = { open: 1, reminder: 1 };
  function emit(action, extra) {
    try { window.dispatchEvent(new CustomEvent('mc:registry', { detail: Object.assign({ action: action, product: root.dataset.product, country: root.dataset.country }, extra || {}) })); } catch (e) { /* old browser */ }
    if (!COUNTED[action]) return;
    var payload = JSON.stringify({ action: 'click', card_key: 'registry-' + action, source: root.dataset.product });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(LOG_URL, new Blob([payload], { type: 'text/plain' }))) return;
    } catch (e) { /* fall through */ }
    try {
      fetch(LOG_URL, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body: payload }).catch(function () { /* best effort */ });
    } catch (e) { /* ignore */ }
  }

  // Preview switch: ?feature=registry turns the chip on for this browser
  // session no matter what the country flag says; ?feature=registry,off
  // turns it back off.
  var preview = false, autoOpen = false;
  try {
    var q = new URLSearchParams(location.search).get('feature') || '';
    if (/(^|,)off(,|$)/.test(q)) { clearCookie('mc_feature'); }
    else if (/(^|,)registry(,|$)/.test(q)) { setCookie('mc_feature', 'registry'); preview = true; }
    // ?feature=registry,open also opens the sheet on load (design review / screenshots).
    if (/(^|,)open(,|$)/.test(q)) autoOpen = true;
  } catch (e) { /* ignore */ }
  if (!preview && cookie('mc_feature') === 'registry') preview = true;

  if (root.dataset.enabled !== 'true' && !preview) return; // flag off here
  // Someone redeeming a code has a deadline. Do not offer them "later".
  if (cookie('discount_code')) return;
  // Already saved on an earlier visit: the return visit is for buying.
  if (cookie('mc_registry')) return;

  var sheet = root.querySelector('[data-mc-registry-sheet]');
  var inner = root.querySelector('.mc-registry__inner');
  var intro = root.querySelector('[data-mc-registry-intro]');
  var done = root.querySelector('[data-mc-registry-done]');
  var emailInput = root.querySelector('[data-mc-registry-form] [name="email"]');
  var opener = root.querySelector('[data-mc-registry-open]');
  if (!sheet || typeof sheet.showModal !== 'function') return; // no <dialog>: leave the chip hidden
  root.hidden = false;

  /* ------------------------------------------------------------ picker */
  // The picker itself (months, days, readout, date field, submit) is the
  // shared module in custom-mamacool-picker.js; this file is the sheet
  // around it. The hooks are the sheet's only involvement.
  if (!window.mcDuePicker) return;
  var picker = window.mcDuePicker.mount(root, {
    api: root.dataset.api,
    product: root.dataset.product,
    country: root.dataset.country,
    surface: 'chip',
    hooks: {
      onLayout: function () { fitSheet(); },
      onFieldFocus: function () { revealField(); },
      onSaved: function () { emit('reminder'); },
      // Close the keyboard first, then show Done once the viewport has grown
      // back (or after 400ms if it never says), then refit so the shorter
      // Done panel sits on the real bottom edge.
      beforeSwap: function (swap) {
        try { document.activeElement && document.activeElement.blur(); } catch (err) { /* ignore */ }
        var settle;
        function go() { clearTimeout(settle); if (window.visualViewport) window.visualViewport.removeEventListener('resize', onGrow); swap(); }
        function onGrow() { clearTimeout(settle); settle = setTimeout(go, 120); }
        if (window.visualViewport) window.visualViewport.addEventListener('resize', onGrow);
        setTimeout(go, 400);
      },
      afterSwap: function () {
        sheet.setAttribute('aria-labelledby', 'mc-registry-done-title');
        fitSheet(); fitNextFrame();
        try { (done.querySelector('button') || done).focus({ preventScroll: true }); } catch (err) { /* ignore */ }
      }
    }
  });
  function reset() {
    picker.reset();
    inner.scrollTop = 0;
    sheet.setAttribute('aria-labelledby', 'mc-registry-title');
  }
  function buildMonths() { picker.rebuild(); }

  /* ---------------------------------------------------------- the sheet */
  var phone = window.matchMedia ? window.matchMedia(PHONE_QUERY) : null;
  var mode = null;            // 'phone' | 'desktop' while open
  var pushedHistory = false;  // we added a history entry for the open sheet
  var closing = false;
  var origin = root;          // where the dialog lives when not on the phone path

  function isPhone() { return !!(phone && phone.matches); }
  function typing() { return document.activeElement === emailInput; }
  // Phone path: the dialog is page content, sized to the visible area.
  function fitSheet() {
    if (mode !== 'phone' || !sheet.open) return;
    var vv = window.visualViewport;
    // With the keyboard up the visual viewport is the truth (the panel must
    // sit on the keyboard); otherwise take the largest height on offer.
    var h = typing() && vv ? vv.height
      : Math.max(window.innerHeight || 0, vv ? vv.height : 0, document.documentElement.clientHeight || 0);
    // pageTop is the visual viewport's top in document coordinates on
    // every browser. On iOS window.scrollY already includes the keyboard
    // offset, so adding visualViewport.offsetTop to it double-counts and
    // drops the sheet hundreds of pixels down the page.
    var top = vv && typeof vv.pageTop === 'number' ? vv.pageTop : (window.scrollY || window.pageYOffset || 0);
    sheet.style.top = Math.round(top) + 'px';
    sheet.style.height = Math.round(h) + 'px';
  }
  var fitTimer, fitFrame = 0;
  function fitSoon() { clearTimeout(fitTimer); fitTimer = setTimeout(fitSheet, 50); }
  function fitNextFrame() { if (fitFrame) return; fitFrame = requestAnimationFrame(function () { fitFrame = 0; fitSheet(); }); }
  // Bring the focused field into view by scrolling the panel itself, never
  // the document (a document scroll would slide the page under the sheet).
  function revealField() {
    var el = document.activeElement;
    if (!el || !inner.contains(el)) return;
    // The email field is the last step: scroll the panel to its end so the
    // field and the Remind me button under it both sit above the keyboard.
    if (el === emailInput) { inner.scrollTop = inner.scrollHeight; return; }
    var r = el.getBoundingClientRect(), box = inner.getBoundingClientRect();
    var over = r.bottom - (box.bottom - 12);
    var under = (box.top + 12) - r.top;
    if (over > 0) inner.scrollTop += over; else if (under > 0) inner.scrollTop -= under;
  }

  // Page freeze: the theme's scroll lock (touchmove is cancelled outside
  // [data-scroll-lock-scrollable] elements, which iOS honours, unlike
  // overflow:hidden on body), plus a body class for the CSS belt.
  function lockPage(on) {
    try {
      document.dispatchEvent(new CustomEvent(on ? 'theme:scroll:lock' : 'theme:scroll:unlock', { bubbles: true, detail: on ? inner : 0 }));
    } catch (e) { /* ignore */ }
    document.body.classList.toggle('mc-registry-open', on);
  }
  // Everything outside the sheet is inert while it is open on the phone
  // path (a modal dialog gets this for free).
  var inerted = [];
  function inertPage(on) {
    if (!on) {
      inerted.forEach(function (rec) {
        if (rec.prop) rec.el.inert = rec.was; else if (rec.had === null) rec.el.removeAttribute('aria-hidden'); else rec.el.setAttribute('aria-hidden', rec.had);
      });
      inerted = [];
      return;
    }
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === sheet || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
      if ('inert' in el) { inerted.push({ el: el, prop: true, was: el.inert }); el.inert = true; }
      else { inerted.push({ el: el, prop: false, had: el.getAttribute('aria-hidden') }); el.setAttribute('aria-hidden', 'true'); }
    }
  }
  // Focus trap: a Tab wrap that reads the focusable set live. The theme's
  // trap snapshots first/last at open, before the day and email steps
  // exist, so it could never reach the field or the button.
  function trap(on) {
    if (on) { sheet.addEventListener('keydown', wrapTab); try { (startFocus() || sheet).focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
    else sheet.removeEventListener('keydown', wrapTab);
  }
  function focusables() {
    return Array.prototype.filter.call(sheet.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'), function (el) {
      return !el.disabled && el.offsetParent !== null && !el.closest('[hidden]');
    });
  }
  function firstFocusable() { var f = focusables(); return f.length ? f[0] : null; }
  // Where focus lands on open: the first month chip (the first thing to
  // do), never the close button.
  function startFocus() { return sheet.querySelector('[data-mc-registry-months] [role="radio"][tabindex="0"]') || firstFocusable(); }
  function wrapTab(e) {
    if (e.key !== 'Tab') return;
    var f = focusables(); if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus({ preventScroll: true }); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus({ preventScroll: true }); }
  }

  function openSheet(keep) {
    if (sheet.open) return;
    if (!keep) { reset(); buildMonths(); }
    mode = isPhone() ? 'phone' : 'desktop';
    if (mode === 'phone') {
      document.body.appendChild(sheet);
      sheet.setAttribute('aria-modal', 'true');
      sheet.show();
      fitSheet(); fitNextFrame(); setTimeout(fitSheet, 300);
      lockPage(true);
      inertPage(true);
    } else {
      if (sheet.parentNode !== origin) origin.appendChild(sheet);
      sheet.showModal();
      lockPage(true);
    }
    trap(true);
    opener.setAttribute('aria-expanded', 'true');
    // Back (Android button, iOS edge swipe) closes the sheet, not the page.
    // Reuse our entry if it is still current (a reopen before the previous
    // close's history.back() has run), never stack a second one.
    try {
      if (!(history.state && history.state.mcRegistry)) history.pushState({ mcRegistry: 1 }, '');
      pushedHistory = true;
    } catch (e) { pushedHistory = false; }
  }
  function closeSheet() {
    if (!sheet.open || closing) return;
    closing = true;
    try { document.activeElement && document.activeElement.blur(); } catch (e) { /* ignore */ }
    sheet.close();
  }
  sheet.addEventListener('close', function () {
    trap(false);
    inertPage(false);
    lockPage(false);
    sheet.style.top = ''; sheet.style.height = '';
    if (sheet.parentNode !== origin) origin.appendChild(sheet);
    mode = null;
    opener.setAttribute('aria-expanded', 'false');
    if (pushedHistory && !rotating) {
      pushedHistory = false;
      try { if (history.state && history.state.mcRegistry) history.back(); } catch (e) { /* ignore */ }
    }
    closing = false;
    if (!rotating) { try { opener.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
  });
  window.addEventListener('popstate', function () {
    if (sheet.open && !(history.state && history.state.mcRegistry)) { pushedHistory = false; closeSheet(); }
  });
  // Escape: a modal dialog closes itself; the phone path needs a hand.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet.open && mode === 'phone') { e.preventDefault(); closeSheet(); }
  });
  // Breakpoint crossed while open (rotation): reopen in the right mode,
  // keeping the date and email she has entered.
  var rotating = false;
  if (phone && phone.addEventListener) {
    phone.addEventListener('change', function () {
      if (!sheet.open) return;
      rotating = true;
      closeSheet();
      setTimeout(function () { openSheet(true); rotating = false; }, 250);
    });
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', fitSoon);
    window.visualViewport.addEventListener('scroll', fitSoon);
  }
  window.addEventListener('resize', fitSoon);
  window.addEventListener('scroll', fitNextFrame, { passive: true });
  window.addEventListener('orientationchange', function () { setTimeout(fitSheet, 250); });
  // Keyboard: one refit as it settles, then the field back into view.
  sheet.addEventListener('focusin', function () { fitSheet(); setTimeout(function () { fitSheet(); revealField(); }, 350); });
  sheet.addEventListener('focusout', function () { setTimeout(fitSheet, 320); });

  opener.addEventListener('click', function () { openSheet(); emit('open'); });
  if (autoOpen) { try { openSheet(); } catch (e) { /* ignore */ } }
  Array.prototype.forEach.call(root.querySelectorAll('[data-mc-registry-close]'), function (b) { b.addEventListener('click', closeSheet); });
  sheet.addEventListener('click', function (e) { if (e.target === sheet) closeSheet(); });

})();
